// Imports for external libraries go here.
import { EditableComponent } from '@adobe/aem-react-editable-components';
import { FC, useCallback, useEffect, useRef } from 'react';
import { useScrollStore, useStickyState } from '@marriott/mi-store-utils';

// Imports for internal (to the monorepo) libraries go here,
// separated by a blank line from external imports.
// The closer the import is to the file the lower it should be in this list.
import { DefaultSecondaryNavigation } from '../../molecules/SecondaryNavigation/DefaultSecondaryNavigation';
import { HWSPrimaryDetachedNavigation } from '../../molecules/SecondaryNavigation/HWSPrimaryDetachedNavigation';
import { StyledSecondaryNavigationContainer, StyledSecondaryNavigationPlaceholder } from './SecondaryNavigation.styles';
import { useSecondaryNavigationStore } from '../../store';
import { SecondaryNavigationProps } from './SecondaryNavigation.types';

export const SecondaryNavigationConfig = {
  emptyLabel: 'SecondaryNavigation',
  isEmpty: () => true,
  resourceType: `${process.env['NEXT_PUBLIC_AEM_SITE']}/components/content/secondarynavigation`,
};

// Use named rather than default exports.
export const SecondaryNavigation: FC<SecondaryNavigationProps> = props => {
  const { model, activeMenuItem } = props;
  const setRegisteredComponent = useScrollStore(state => state.setRegisteredComponent);
  const navComponentOrder = useScrollStore(state => state.navComponentOrder);
  const scrollDirection = useScrollStore(state => state.scrollDirection);
  const prevScrollDirection = useScrollStore(state => state.prevScrollDirection);
  const { isSticky, top } = useStickyState(model?.componentId as string);
  const setSecondaryNavigationData = useSecondaryNavigationStore(state => state.setSecondaryNavigationData);
  const clearSecondaryNavigationData = useSecondaryNavigationStore(state => state.clearSecondaryNavigationData);
  const secondaryNavigationContainerRef = useRef<HTMLDivElement | null>(null);
  const componentId = model?.componentId;
  const isStickyOnScrollUp = !!model?.scrollSetting?.isStickyOnScrollUp;
  const isStickyOnScrollDown = !!model?.scrollSetting?.isStickyOnScrollDown;
  const isScrollingUp = scrollDirection === 'up' || (scrollDirection === '' && prevScrollDirection === 'up');
  const isScrollingDown = scrollDirection === 'down' || (scrollDirection === '' && prevScrollDirection === 'down');

  const registerSecondaryNavigationComponent = useCallback(
    (ref: HTMLDivElement) => {
      if (!ref || !componentId) {
        return;
      }

      setRegisteredComponent({
        componentId,
        isStickyOnScrollUp,
        isStickyOnScrollDown,
        height: ref.clientHeight,
      });
    },
    [componentId, isStickyOnScrollDown, isStickyOnScrollUp, setRegisteredComponent]
  );

  const secondaryNavigationRef = useCallback(
    (ref: HTMLDivElement) => {
      if (!ref) {
        return;
      }
      secondaryNavigationContainerRef.current = ref;
      registerSecondaryNavigationComponent(ref);
    },
    [registerSecondaryNavigationComponent]
  );

  useEffect(() => {
    if (!secondaryNavigationContainerRef.current || !componentId || navComponentOrder.length === 0) {
      return;
    }
    registerSecondaryNavigationComponent(secondaryNavigationContainerRef.current);
  }, [componentId, navComponentOrder.length, registerSecondaryNavigationComponent]);

  useEffect(() => {
    if (!secondaryNavigationContainerRef.current || !componentId || typeof ResizeObserver === 'undefined') {
      return;
    }
    const resizeObserver = new ResizeObserver(() => {
      if (secondaryNavigationContainerRef.current) {
        registerSecondaryNavigationComponent(secondaryNavigationContainerRef.current);
      }
    });
    resizeObserver.observe(secondaryNavigationContainerRef.current);
    return () => resizeObserver.disconnect();
  }, [componentId, registerSecondaryNavigationComponent]);

  useEffect(() => {
    if (model) {
      setSecondaryNavigationData({
        model,
        activeMenuItem,
      });
    }

    return () => {
      clearSecondaryNavigationData();
    };
  }, [model, activeMenuItem, setSecondaryNavigationData, clearSecondaryNavigationData]);

  return (
    <>
      <StyledSecondaryNavigationContainer
        ref={secondaryNavigationRef}
        isSticky={isSticky}
        top={top}
        shouldAnimateTop={isSticky && isScrollingUp && top !== 0}
        shouldAnimateBottom={isSticky && isScrollingDown}
      >
        <EditableComponent config={SecondaryNavigationConfig} {...props}>
          {model?.variation === 'default navigation' ? (
            <DefaultSecondaryNavigation {...model} activeMenuItem={activeMenuItem} />
          ) : (
            <HWSPrimaryDetachedNavigation {...model} activeMenuItem={activeMenuItem} />
          )}
        </EditableComponent>
      </StyledSecondaryNavigationContainer>
      {isSticky && <StyledSecondaryNavigationPlaceholder />}
    </>
  );
};
